import { otpSend } from '@/actions/ecom/login';
import { getStaticContent } from '@/actions/ecom/staticContent';
import { verifyLead } from '@/actions/verifyLead';
import PageLayout from '@/components/Layouts/PageLayout';
import VerifyOTP from '@/components/VerifyOTP';
import useFormRenderer, { FormField } from '@/hooks/useFormRenderer';
import useToast from '@/hooks/useToast';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import AppBar from '@/shared/AppBar';
import Container from '@/shared/Container';
import Div from '@/shared/Div';
import Heading from '@/shared/Heading';
import Paragraph from '@/shared/Paragraph';
import { SeoData } from '@/types/seo';
import { MapType } from '@/types';
import BlueGradientBackground from '@/components/Icons/BlueGradientBackground';
import { GetServerSidePropsContext } from 'next';
import React, { Fragment, useEffect } from 'react';
import Image from '@/shared/Image';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames, SCREEN_NAMES } from '@/types/gtm';
import { fetchPincodeData } from '@/actions/fetchPincodeData';
import CheckCircle from '@/components/Icons/CheckCircleDemo';
import { NOT_FOUND } from '@/constants/app';
import Cookies from '@/utils/cookie';

interface PageProps {
  config: {
    FORM_FIELDS: FormField[];
    PRODUCT_TYPE: Record<string, any>;
    PRODUCT_ENQUIRY_SUB_CATEGORIES: Record<string, any>;
    subTitle: string;
    title: string;
  };
  imageConfig: {
    MOB_PRODUCT_CATEGORY_IMAGE: MapType;
    DSK_PRODUCT_CATEGORY_IMAGE: MapType;
  };
  seo: SeoData;
}

const ProductEnquiryForm = ({ page }: { page: PageProps }) => {
  const { FORM_FIELDS = [], PRODUCT_TYPE = [] as any, subTitle, title } = page.config;
  const { MOB_PRODUCT_CATEGORY_IMAGE, DSK_PRODUCT_CATEGORY_IMAGE } = page.imageConfig;

  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showOtpScreen, setShowOtpScreen] = React.useState(false);
  const fetch = (...args: any) => verifyLead.apply(null, args);
  const { fireGTMEvent } = useGTM();
  const verifyLeadRequest = useTriggerRequest({ fetch });

  const OTP = useTriggerRequest({ fetch: (...args: any) => otpSend.apply(null, args) });
  const { triggerError } = useToast();
  const submitHandler = async (data: any) => {
    try {
      await OTP.promisifyTrigger({ mobile_number: data.mobileNumber });
      setShowOtpScreen(true);
    } catch (error: any) {
      triggerError({ message: error?.error, position: 'top' });
    }
  };

  const { tsx, form, updateFieldById } = useFormRenderer({
    fields: FORM_FIELDS,
    className: 'w-full grid grid-cols-2 gap-s5',
    submitHandler,
  });

  const onVerify = async (otp: string) => {
    try {
      verifyLeadRequest.reset();
      const { mobileNumber, productCategory, productType, pincode, ...restFormValues } =
        form.getValues();
      const payload = {
        leadType: 'ENQUIRY',
        lead: {
          ...restFormValues,
          // productCategory: [productCategory?.id],
          phone: mobileNumber,
          // productType: productType.id,
          leadType: 'ENQUIRY',
          pincode: pincode,
        },
        verify: {
          mobileNumber,
          otp,
          userAgent: 'WEB',
        },
        utm: {
          utmSource: Cookies.get('utm_source'),
          utmMedium: Cookies.get('utm_medium'),
          utmChannel: Cookies.get('utm_channel'),
          utmCampaign: Cookies.get('utm_campaign'),
          utmTerm: Cookies.get('utm_term'),
        },
      };
      const response = await verifyLeadRequest.promisifyTrigger(payload);
      const responsePhoneNumber = response?.phone;
      fireGTMEvent(
        GTMEventNames.ENQUIRY_LEAD_SUBMITTED,
        {
          mobile_number: responsePhoneNumber,
          lead_type: 'ENQUIRY',
          screen_name: SCREEN_NAMES.PRODUCT_ENQUIRY,
        },
        {
          webengage: {
            fire: true,
            isDirect: true,
          },
        },
      );

      setShowSuccess(true);
    } catch (error: any) {
      if (error?.detail?.code) {
        form.setError(error?.detail?.code, { message: error?.detail?.details });
        setShowOtpScreen(false);
        verifyLeadRequest.reset();
      }
      triggerError({ message: error?.error, position: 'top' });
    }
  };
  const mobileNumber = form.watch('mobileNumber') as any;
  const productCategory = form.watch('productCategory') as any;
  const productType = form.watch('productType') as any;
  const pincode = form.watch('pincode') as string;
  const mobImage = MOB_PRODUCT_CATEGORY_IMAGE[productCategory?.id ?? 'default'];
  const dskImage = DSK_PRODUCT_CATEGORY_IMAGE[productCategory?.id ?? 'default'];
  const pincodeData = useTriggerRequest({
    fetch: (...args: any) => fetchPincodeData.apply(null, args),
  });

  useEffect(() => {
    if (pincode && pincode.length === 6) {
      pincodeData
        .promisifyTrigger(pincode)
        .then((response) => {
          form.setValue('city', response.city);
          form.setValue('state', response.state);
        })
        .catch((error) => {
          triggerError?.({
            message: 'Please enter a different pincode.',
            position: 'top',
          });
          form.setValue('city', '');
          form.setValue('state', '');
        });
    } else {
      form.setValue('city', '');
      form.setValue('state', '');
    }
  }, [pincode]);

  useEffect(() => {
    if (!productCategory?.id) form.resetField('productCategory');
    updateFieldById('productType', {
      options: PRODUCT_TYPE[productCategory?.id] || [],
    });
    form.resetField('productType');
  }, [productCategory?.id]);

  useEffect(() => {
    updateFieldById('enquiry_form_submit', {
      isLoading: OTP.isLoading,
    });
  }, [OTP.isLoading]);

  const renderVerifyOTP = () => {
    if (showOtpScreen && !showSuccess) {
      return (
        <Fragment>
          <AppBar className="!-ml-6" onClick={() => setShowOtpScreen(false)} />
          <Div className="md:mt-s6">
            <VerifyOTP
              title="Verify your phone number"
              subTitle={`Enter the code we just sent to <b>${mobileNumber ?? ''}</b>`}
              mobile={mobileNumber}
              onVerify={onVerify}
              verifyLeadRequest={verifyLeadRequest}
            />
          </Div>
        </Fragment>
      );
    }
    return null;
  };
  const renderSuccessScreen = () => {
    if (!showSuccess) return null;
    return (
      <Fragment>
        <CheckCircle className="text-success" />
        <Div className="flex flex-col gap-s4">
          <Heading className=" mt-s7 text-h-2 md:text-hd-1 text-cti-primary">
            Thank you for reaching out!
          </Heading>
          <Paragraph className="text-body-lg text-cti-primary ">
            Our team will be in touch soon to schedule your demo.
            <br />
            {`We can't wait for you to try our products!`}
          </Paragraph>
        </Div>
      </Fragment>
    );
  };

  const renderProductEnquiryForm = () => {
    if (showOtpScreen) return null;
    return (
      <Div>
        <Div className="flex flex-col gap-s6 md:max-w-xl items-start self-start w-full">
          <Div className="flex flex-col gap-s4">
            <Paragraph className="text-h-2 md:text-hd-2">{title}</Paragraph>
            <Paragraph className="text-body-lg col-span-2">{subTitle}</Paragraph>
          </Div>
          {tsx}
        </Div>
      </Div>
    );
  };
  return (
    <PageLayout seo={page.seo}>
      <BlueGradientBackground className="hidden md:block absolute top-0 left-0 -z-10 scale-x-[-1]" />
      <Div className="w-full md:hidden">
        <Image className="w-full h-auto object-contain" src={mobImage} alt="Book a Demo" />
      </Div>

      <Container className="flex flex-col md:flex-row w-full py-s6 gap-s9 ">
        <Div className="hidden md:block w-full md:w-1/2">
          <Image
            className="w-full h-auto object-contain rounded-cxs"
            src={dskImage}
            alt="Book a Demo"
          />
        </Div>
        <Div className="md:w-1/2">
          {renderVerifyOTP()}
          {showSuccess ? renderSuccessScreen() : renderProductEnquiryForm()}
        </Div>
      </Container>
    </PageLayout>
  );
};

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  try {
    const page = await getStaticContent('/product-enquiry');
    if (!page.config?.FORM_FIELDS || !page.config?.PRODUCT_TYPE) {
      throw new Error('Required form configuration is missing');
    }
    return {
      props: {
        page,
      },
    };
  } catch (error) {
    return NOT_FOUND;
  }
};

export default ProductEnquiryForm;
